import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { addScormContent, uploadScormZip } from '../../../services/adminService';

function ScormUploadModal({ moduleId, onSuccess, onClose }) {
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [earningPoints, setEarningPoints] = useState('0');
    const [parsedPackage, setParsedPackage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [statusText, setStatusText] = useState('');
    const [statusSteps, setStatusSteps] = useState([]);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;
        if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
            toast.warning('Only .zip SCORM packages are supported', { position: 'top-center' });
            event.target.value = '';
            return;
        }
        setFile(selectedFile);
    };

    const handleUpload = async () => {
        if (!title.trim()) {
            toast.warning('SCORM title is required', { position: 'top-center' });
            return;
        }
        if (!file) {
            toast.warning('SCORM ZIP file is required', { position: 'top-center' });
            return;
        }

        setLoading(true);
        setUploadProgress(0);
        setStatusText('Preparing SCORM ZIP upload...');
        setStatusSteps(['Preparing upload']);
        let backendTimer;
        try {
            const formData = new FormData();
            formData.append('scorm_zip', file);
            formData.append('title', title.trim());

            const response = await uploadScormZip(formData, {
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                    const safePercent = Math.min(percent, 95);
                    setUploadProgress(safePercent);
                    setStatusText(safePercent < 95 ? `Uploading ZIP to server... ${safePercent}%` : 'ZIP uploaded. Backend is unzipping and validating SCORM package...');
                    setStatusSteps((prev) => prev.includes('Uploading ZIP to server') ? prev : [...prev, 'Uploading ZIP to server']);
                    if (safePercent >= 95 && !backendTimer) {
                        const backendSteps = [
                            'Unzipping package safely',
                            'Reading imsmanifest.xml',
                            'Uploading original ZIP to S3',
                            'Uploading extracted SCORM files to S3',
                            'Finalizing launch metadata'
                        ];
                        let index = 0;
                        backendTimer = setInterval(() => {
                            const nextStep = backendSteps[index];
                            if (!nextStep) return;
                            setStatusText(`${nextStep}...`);
                            setStatusSteps((prev) => prev.includes(nextStep) ? prev : [...prev, nextStep]);
                            index += 1;
                        }, 1800);
                    }
                }
            });

            if (backendTimer) clearInterval(backendTimer);

            if (response?.success) {
                setUploadProgress(100);
                setStatusText('SCORM package parsed and uploaded to S3 successfully.');
                setStatusSteps((prev) => prev.includes('Completed') ? prev : [...prev, 'Completed']);
                setParsedPackage(response.data);
                setStep(2);
                toast.success('SCORM package parsed successfully', { position: 'top-center' });
            } else {
                toast.error(response?.data?.error_msg || 'SCORM upload failed', { position: 'top-center' });
            }
        } catch (error) {
            if (backendTimer) clearInterval(backendTimer);
            setStatusText('SCORM upload failed.');
            toast.error(error.response?.data?.data?.error_msg || 'SCORM upload failed', { position: 'top-center' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!parsedPackage?.id || !parsedPackage?.launch_url) {
            toast.error('Parsed SCORM package data is missing', { position: 'top-center' });
            return;
        }

        setLoading(true);
        setStatusText('Saving SCORM content to this module...');
        try {
            const response = await addScormContent({
                module_id: moduleId,
                title: title.trim(),
                scorm_package_id: parsedPackage.id,
                launch_url: parsedPackage.launch_url,
                version: parsedPackage.version || '1.2',
                earning_points: parseInt(earningPoints, 10) || 0,
                description: parsedPackage.description || '',
                package_url: parsedPackage.package_url || null,
                package_s3_key: parsedPackage.package_s3_key || null,
                s3_prefix: parsedPackage.s3_prefix || null,
                manifest_path: parsedPackage.manifest_path || null,
                launch_path: parsedPackage.launch_path || null,
                original_file_name: parsedPackage.original_file_name || null
            });

            if (response?.success) {
                toast.success('SCORM content added to module', { position: 'top-center' });
                onSuccess?.();
                onClose?.();
            } else {
                toast.error(response?.data?.error_msg || 'Failed to add SCORM content', { position: 'top-center' });
            }
        } catch (error) {
            toast.error(error.response?.data?.data?.error_msg || 'Failed to add SCORM content', { position: 'top-center' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h5 className="text-lg font-semibold text-gray-900">{step === 1 ? 'Upload SCORM Package' : 'Confirm SCORM Content'}</h5>
                        <p className="text-xs text-gray-500 mt-1">Add a SCORM ZIP package as module content.</p>
                    </div>
                    <button type="button" className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700" onClick={onClose} disabled={loading}>
                        <Icon icon="fluent:dismiss-24-regular" width={18} height={18} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {step === 1 ? (
                        <>
                            <div>
                                <label className="block text-sm font-medium mb-1">Content Title *</label>
                                <input className="w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 border-input-border" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter SCORM content title" maxLength={100} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">SCORM ZIP File *</label>
                                <label className="flex flex-col items-center justify-center min-h-[150px] rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
                                    <Icon icon="fluent:archive-24-regular" width={32} height={32} className="text-[#4F6BFF] mb-2" />
                                    <span className="text-sm font-medium text-gray-700">{file ? file.name : 'Choose SCORM ZIP'}</span>
                                    <span className="text-xs text-gray-500 mt-1">Package must contain imsmanifest.xml</span>
                                    <input type="file" accept=".zip" className="hidden" onChange={handleFileChange} disabled={loading} />
                                </label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Earning Points</label>
                                <input type="number" min="0" className="w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 border-input-border" value={earningPoints} onChange={(e) => setEarningPoints(e.target.value)} />
                            </div>
                            {loading && (
                                <div className="space-y-3 rounded-xl border border-blue-100 bg-blue-50 p-3">
                                    <div className="h-2 rounded-full bg-white overflow-hidden">
                                        <div className="h-full bg-[#4F6BFF] transition-all" style={{ width: `${uploadProgress}%` }} />
                                    </div>
                                    <p className="text-xs font-semibold text-blue-800">{statusText || `Uploading and parsing package... ${uploadProgress}%`}</p>
                                    <div className="space-y-1">
                                        {statusSteps.map((item) => (
                                            <div key={item} className="flex items-center gap-2 text-[11px] text-blue-700">
                                                <Icon icon="fluent:checkmark-circle-24-filled" width={13} height={13} />
                                                <span>{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="space-y-4">
                            {loading && (
                                <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs font-semibold text-blue-800">
                                    {statusText || 'Saving SCORM content to this module...'}
                                </div>
                            )}
                            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                                <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                                    <Icon icon="fluent:checkmark-circle-24-filled" width={18} height={18} />
                                    Package parsed successfully
                                </div>
                                <div className="mt-3 space-y-1 text-xs text-green-800 break-all">
                                    <p><span className="font-semibold">Package ID:</span> {parsedPackage?.id}</p>
                                    <p><span className="font-semibold">Launch URL:</span> {parsedPackage?.launch_url}</p>
                                    <p><span className="font-semibold">Version:</span> SCORM {parsedPackage?.version || '1.2'}</p>
                                    {parsedPackage?.package_url && <p><span className="font-semibold">Package Storage:</span> S3</p>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                    <button type="button" className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 disabled:opacity-50" onClick={step === 1 ? onClose : () => setStep(1)} disabled={loading}>
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>
                    <button type="button" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white text-sm font-medium shadow-sm disabled:opacity-50" onClick={step === 1 ? handleUpload : handleSave} disabled={loading}>
                        {loading && <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" role="status" aria-hidden="true" />}
                        <span>{step === 1 ? (loading ? 'Uploading...' : 'Upload & Parse') : (loading ? 'Saving...' : 'Add to Module')}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ScormUploadModal;
